import { useState } from "react";
import { getUsersApi, updateTenantSettings } from "@praesto/core";
import type { ChannelId, NotificationType, TenantRole } from "@praesto/shared-types";
import { Badge, Button, Card, SectionHeading, Tabs } from "@praesto/ui-kit";
import { useCore } from "../lib/core";
import { ROLE_ORDER } from "../people/roleLabels";
import { ChannelCell } from "./ChannelCell";
import { CONFIGURABLE_NOTIFICATION_TYPES } from "./notification-types";
import { cellState, setCell, toChannels, type Rules } from "./rules-model";

interface TenantNotifBase {
  readonly enabled: boolean;
  readonly channels: { readonly email: boolean; readonly push: boolean };
}
// Matches the server's DEFAULT_TENANT_GATE — used only when the tenant has no
// `notifications` settings yet, so enabled/channels are preserved on save.
const DEFAULT_TENANT_NOTIF: TenantNotifBase = {
  enabled: true,
  channels: { email: true, push: true },
};

// Area order follows first-occurrence in the catalogue (shifts, monitoring, procedure) —
// derived rather than duplicated so a Task 8 catalogue change can't silently drift.
const AREAS = Array.from(new Set(CONFIGURABLE_NOTIFICATION_TYPES.map((t) => t.area)));

type SaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * One (role, type) cell: computes state/active over `Rules` and delegates
 * rendering to the shared `ChannelCell` (also used by Task 10's personal
 * per-type grid, which points the same presentational cell at a flat
 * `PerType` map instead of a role-keyed `Rules`).
 */
function NotificationRuleCell(props: {
  readonly rules: Rules | undefined;
  readonly role: TenantRole;
  readonly type: NotificationType;
  readonly onChange: (next: Rules) => void;
}) {
  const { rules, role, type, onChange } = props;
  const state = cellState(rules, role, type);
  const active = toChannels(rules, role, type) ?? [];

  function toggle(channel: ChannelId) {
    const next = active.includes(channel)
      ? active.filter((c) => c !== channel)
      : [...active, channel];
    onChange(setCell(rules, role, type, next));
  }

  return (
    <ChannelCell
      state={state}
      active={active}
      onToggle={toggle}
      onReset={() => onChange(setCell(rules, role, type, undefined))}
    />
  );
}

/**
 * Owner-only admin page: per (role x type) default notification routing.
 * A role tab shows `CONFIGURABLE_NOTIFICATION_TYPES` grouped by area; each
 * row is a type label + `NotificationRuleCell`. Saving sends the FULL
 * `notifications` block (enabled + channels preserved from the current
 * tenant settings, since the server replaces the field wholesale).
 */
export function NotificationRulesSettings() {
  const core = useCore();
  const t = core.i18n.t;
  const isOwner = core.rbac.can("manage", "hotel");

  const initial = (() => {
    try {
      return core.tenant.settings().notifications ?? null;
    } catch {
      return null;
    }
  })();
  const [rules, setRules] = useState<Rules | undefined>(initial?.rules);
  const [role, setRole] = useState<TenantRole>(ROLE_ORDER[0]);
  const [status, setStatus] = useState<SaveStatus>("idle");

  if (!isOwner) return null;

  async function handleSave(): Promise<void> {
    setStatus("saving");
    try {
      // Read enabled/channels FRESH from reactive settings rather than the
      // mount-time `initial` snapshot: the server replaces `notifications`
      // wholesale, so saving here would otherwise revert a gate change made
      // by the owner (in the personal Notifiche panel) after this page
      // mounted. Defaults mirror the server's DEFAULT_TENANT_GATE.
      const currentBase = (() => {
        try {
          return core.tenant.settings().notifications ?? null;
        } catch {
          return null;
        }
      })();
      const enabled = currentBase?.enabled ?? DEFAULT_TENANT_NOTIF.enabled;
      const channels = currentBase?.channels ?? DEFAULT_TENANT_NOTIF.channels;
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await updateTenantSettings(api.apiBase, { notifications: { enabled, channels, rules } }, tok);
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch {
      setStatus("error");
    }
  }

  const typesByArea = AREAS.map((area) => ({
    area,
    types: CONFIGURABLE_NOTIFICATION_TYPES.filter((x) => x.area === area),
  }));

  return (
    <Card>
      <div className="mb-1 flex items-center justify-between gap-3">
        <SectionHeading
          level={3}
          title={t("settings.notifications.rules.title")}
          description={t("settings.notifications.rules.description")}
        />
        <div className="flex items-center gap-2 flex-shrink-0">
          {status === "saving" && (
            <Badge tone="neutral" variant="soft" size="xs">
              {t("settings.saving")}
            </Badge>
          )}
          {status === "saved" && (
            <Badge tone="success" variant="soft" size="xs">
              {t("settings.saved")}
            </Badge>
          )}
          {status === "error" && (
            <Badge tone="danger" variant="soft" size="xs">
              {t("settings.error")}
            </Badge>
          )}
          <Button
            size="sm"
            variant="primary"
            onClick={() => void handleSave()}
            disabled={status === "saving"}
          >
            {t("settings.notifications.rules.save")}
          </Button>
        </div>
      </div>

      <Tabs
        items={ROLE_ORDER.map((r) => ({
          id: r,
          label: t(`settings.notifications.rules.role.${r}`),
        }))}
        active={role}
        onChange={(id) => setRole(id as TenantRole)}
      />

      <div className="mt-4 space-y-6">
        {typesByArea.map(({ area, types }) => (
          <div key={area}>
            <div className="mb-2 text-eyebrow">
              {t(`settings.notifications.rules.area.${area}`)}
            </div>
            <div className="divide-y divide-border-default border-t border-border-default">
              {types.map(({ type }) => (
                <div
                  key={type}
                  className="flex flex-col gap-1.5 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                >
                  <span className="text-sm text-fg-default">
                    {t(`settings.notifications.rules.type.${type}`)}
                  </span>
                  <NotificationRuleCell rules={rules} role={role} type={type} onChange={setRules} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
